import { useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  acknowledgeAlert,
  alertSupportsMapFocus,
  fetchAlertQueue,
  formatAlertLocation,
  formatAlertType,
  humanizeLabel,
  isGeofenceExitAlert,
  isSosAlert,
  type AlertRow,
} from "../data/alert-queue";
import { cadRealtimeFeed } from "../data/cad-feed";
import { useReadModel } from "../data/useReadModel";
import { useAnalytics } from "../lib/analytics-context";
import { formatDisplayTime } from "../lib/display-time";

// Alert Queue surface — open/acknowledged/resolved alerts with severity weighting and redacted
// guard labels. Open alerts expose an Acknowledge action (acknowledge_alert_event); authorization
// is enforced server-side, the queue refreshes on success, and failures show safe generic copy.
export function AlertQueue({
  client,
  accountId,
  timeZone,
  pollMs = 30000,
  enableRealtime = true,
  selectedRouteAssignmentId = null,
  selectedAlertEventId = null,
  onFocusOnMap,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
  enableRealtime?: boolean;
  selectedRouteAssignmentId?: string | null;
  selectedAlertEventId?: string | null;
  onFocusOnMap?: (alert: AlertRow) => void;
}) {
  const { status, data, error, reload } = useReadModel<AlertRow[]>(
    () => fetchAlertQueue(client, accountId),
    [client, accountId],
    {
      pollMs,
      ...(enableRealtime
        ? { realtime: cadRealtimeFeed(client, accountId, "alerts", "alert_events") }
        : {}),
    },
  );
  const [ackingId, setAckingId] = useState<string | null>(null);
  const [ackError, setAckError] = useState<string | null>(null);
  const analytics = useAnalytics();

  async function onAcknowledge(alertEventId: string): Promise<void> {
    setAckError(null);
    setAckingId(alertEventId);
    const result = await acknowledgeAlert(client, accountId, alertEventId);
    setAckingId(null);
    if (result.ok) {
      analytics.capture("alert_acknowledged");
      reload();
    } else {
      setAckError(result.reason);
    }
  }

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading alerts…</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load alerts."}
      </p>
    );
  }

  const rows = data ?? [];
  if (rows.length === 0) {
    return <p className="surface__state surface__state--ok" role="status">All clear — no alerts.</p>;
  }

  return (
    <div className="alerts-wrap">
      {ackError ? (
        <p className="surface__state surface__state--error" role="alert">
          {ackError}
        </p>
      ) : null}
      <ul className="alerts">
        {rows.map((alert, index) => {
          // SOS may only stand down from the SOS desk (structured outcome required by the RPC).
          const canAcknowledge =
            alert.status === "open"
            && alert.alertEventId.length > 0
            && !isSosAlert(alert.alertType);
          const isAcking = ackingId === alert.alertEventId;
          const sos = isSosAlert(alert.alertType);
          const geofenceExit = isGeofenceExitAlert(alert.alertType);
          const canFocusOnMap = alertSupportsMapFocus(alert) && typeof onFocusOnMap === "function";
          const mapFocused =
            selectedRouteAssignmentId !== null &&
            alert.routeAssignmentId !== null &&
            selectedRouteAssignmentId === alert.routeAssignmentId;
          const deepLinked = selectedAlertEventId === alert.alertEventId;
          const itemClass = [
            "alert__item",
            `alert__item--${alert.severity}`,
            sos ? "alert__item--sos" : "",
            geofenceExit ? "alert__item--geofence-exit" : "",
            mapFocused ? "alert__item--map-focused" : "",
            deepLinked ? "alert__item--deep-linked" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <li
              key={alert.alertEventId || `${alert.detectedAt}-${index}`}
              className={itemClass}
              aria-current={deepLinked ? "true" : undefined}
            >
              <div className="alert__head">
                <span
                  className={`alert__type${sos ? " alert__type--sos" : ""}${
                    geofenceExit ? " alert__type--geofence-exit" : ""
                  }`}
                >
                  {sos ? <span className="alert__sos-dot" aria-hidden="true" /> : null}
                  {geofenceExit ? <span className="alert__geofence-dot" aria-hidden="true" /> : null}
                  {formatAlertType(alert.alertType)}
                </span>
                <span className={`alert__status alert__status--${alert.status}`}>
                  {humanizeLabel(alert.status)}
                </span>
              </div>
              <div className="alert__where">
                {formatAlertLocation(alert)}
              </div>
              <div className="alert__meta">
                {alert.guardLabel ?? "—"} · {formatDisplayTime(alert.detectedAt, timeZone)}
              </div>
              <div className="alert__actions">
                {canFocusOnMap ? (
                  <button
                    className="alert__map-focus"
                    type="button"
                    aria-pressed={mapFocused}
                    aria-label={`View ${formatAlertType(alert.alertType)} on live map`}
                    onClick={() => onFocusOnMap?.(alert)}
                  >
                    View on map
                  </button>
                ) : null}
                {canAcknowledge ? (
                  <button
                    className="alert__ack"
                    type="button"
                    disabled={isAcking}
                    aria-busy={isAcking}
                    aria-label={`Acknowledge ${formatAlertType(alert.alertType)}${
                      alert.checkpointName ? ` at ${alert.checkpointName}` : ""
                    }`}
                    onClick={() => void onAcknowledge(alert.alertEventId)}
                  >
                    {isAcking ? "Acknowledging…" : "Acknowledge"}
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
